import { computed, Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';

import { AuthSelectorsFacade } from '@ps/auth-data-access';

import { BuyerSettings } from '../models/settings.model';
import { FeatureEntitlement } from '../models/entitlement.model';
import { getBuyerSettings } from '../utils/buyer-settings.helper';
import { filterEntitlementsForUser } from '../utils/entitlement.helper';
import {
  selectAllBuyerSettings,
  selectCompanySettings,
  selectCurrencySettings,
  selectEntitlements,
  selectError,
  selectLoading,
  selectPreferences,
  selectPreferencesError,
  selectPreferencesLoading,
  selectSelectedPreferences,
  selectSelectedMobilePreferences,
  selectDefaultPreferences,
  selectDashboardCacheStatus,
  selectSelectedDesktopPreferences,
  selectPreferencesCachedAt,
  selectReportPreferences,
  selectOrderedReportPreferences,
  selectBillingRuleRecord,
} from './user-settings.selectors';

@Injectable({ providedIn: 'root' })
export class UserSettingsSelectorsFacade {
  private readonly _store = inject(Store);
  private readonly _auth = inject(AuthSelectorsFacade);

  private readonly _preferencesError = this._store.selectSignal(
    selectPreferencesError,
  );

  // --- Settings + entitlements ---

  public readonly allBuyerSettings = this._store.selectSignal(
    selectAllBuyerSettings,
  );

  public readonly companySettings = this._store.selectSignal(
    selectCompanySettings,
  );

  /** Currency details for the active context (operator's own, or the impersonated buyer's). */
  public readonly currencySettings = this._store.selectSignal(
    selectCurrencySettings,
  );

  /** All entitlements for the platform. */
  public readonly entitlements = this._store.selectSignal(selectEntitlements);

  public readonly loading = this._store.selectSignal(selectLoading);

  public readonly error = this._store.selectSignal(selectError);

  /** Buyer feature flags for the active context (impersonated buyer, or the current user's own company). */
  public readonly buyerSettings = computed((): BuyerSettings => {
    const impersonatedBuyer = this._auth.impersonatedBuyer();

    return this._auth
      .user()
      .map((user) => impersonatedBuyer?.id ?? user?.companyId)
      .mapInto((companyId) =>
        Option.some(getBuyerSettings(this.allBuyerSettings(), companyId)),
      )
      .unwrapOr({});
  });

  /** Entitlements filtered down to the current user's access. */
  public readonly userEntitlements = computed((): FeatureEntitlement[] =>
    this._auth
      .user()
      .map((user) => filterEntitlementsForUser(this.entitlements(), user))
      .unwrapOr([]),
  );

  // --- Dashboard preferences ---

  public readonly preferences = this._store.selectSignal(selectPreferences);

  public readonly selectedPreferences = this._store.selectSignal(
    selectSelectedPreferences,
  );

  public readonly selectedDesktopPreferences = this._store.selectSignal(
    selectSelectedDesktopPreferences,
  );

  public readonly selectedMobilePreferences = this._store.selectSignal(
    selectSelectedMobilePreferences,
  );

  public readonly defaultPreferences = this._store.selectSignal(
    selectDefaultPreferences,
  );

  public readonly preferencesLoading = this._store.selectSignal(
    selectPreferencesLoading,
  );

  public readonly preferencesError = computed(() =>
    Option.from(this._preferencesError()),
  );

  public readonly preferencesCachedAt = this._store.selectSignal(
    selectPreferencesCachedAt,
  );

  public readonly preferencesCacheStatus = this._store.selectSignal(
    selectDashboardCacheStatus,
  );

  public readonly reportPreferences = this._store.selectSignal(
    selectReportPreferences,
  );

  public readonly reportOrderedPreferences = this._store.selectSignal(
    selectOrderedReportPreferences,
  );

  public readonly billingRuleRecord = this._store.selectSignal(
    selectBillingRuleRecord,
  );
}
